/**
 * Marker Templates Component
 * Pre-built templates for common property types
 */
import {
  HiOutlineOfficeBuilding,
  HiOutlineHome,
  HiOutlineLibrary,
  HiOutlineLocationMarker,
  HiOutlineShoppingBag,
  HiOutlineAcademicCap,
  HiOutlineHeart,
  HiOutlineGlobe,
  HiOutlineSparkles,
  HiOutlineCurrencyDollar,
} from 'react-icons/hi'
import clsx from 'clsx'

// Template categories
const TEMPLATE_CATEGORIES = [
  { id: 'bds', label: 'Bất động sản', icon: HiOutlineOfficeBuilding },
  { id: 'tien_ich', label: 'Tiện ích', icon: HiOutlineSparkles },
  { id: 'khac', label: 'Khác', icon: HiOutlineGlobe },
]

// Pre-built marker templates
const MARKER_TEMPLATES = [
  // Bất động sản templates
  {
    id: 'can_ho',
    category: 'bds',
    name: 'Căn hộ chung cư',
    icon: '🏢',
    color: '#3B82F6',
    marker_type: 'ban',
    property_type: 'can_ho',
    suggested_fields: ['price_text', 'area_text', 'bedrooms'],
  },
  {
    id: 'nha_pho',
    category: 'bds',
    name: 'Nhà phố',
    icon: '🏠',
    color: '#10B981',
    marker_type: 'ban',
    property_type: 'nha_pho',
    suggested_fields: ['price_text', 'area_text', 'bedrooms'],
  },
  {
    id: 'biet_thu',
    category: 'bds',
    name: 'Biệt thự',
    icon: '🏡',
    color: '#8B5CF6',
    marker_type: 'ban',
    property_type: 'biet_thu',
    suggested_fields: ['price_text', 'area_text', 'bedrooms'],
  },
  {
    id: 'dat_nen',
    category: 'bds',
    name: 'Đất nền',
    icon: '🌍',
    color: '#F59E0B',
    marker_type: 'ban',
    property_type: 'dat_nen',
    suggested_fields: ['price_text', 'area_text'],
  },
  {
    id: 'mat_bang',
    category: 'bds',
    name: 'Mặt bằng kinh doanh',
    icon: '🏪',
    color: '#EF4444',
    marker_type: 'thue',
    property_type: 'mat_bang',
    suggested_fields: ['rent_price_text', 'area_text'],
  },
  {
    id: 'van_phong',
    category: 'bds',
    name: 'Văn phòng',
    icon: '💼',
    color: '#6366F1',
    marker_type: 'thue',
    property_type: 'van_phong',
    suggested_fields: ['rent_price_text', 'area_text'],
  },
  {
    id: 'phong_tro',
    category: 'bds',
    name: 'Phòng trọ',
    icon: '🛏️',
    color: '#EC4899',
    marker_type: 'thue',
    property_type: 'phong_tro',
    suggested_fields: ['rent_price_text', 'area_text'],
  },
  {
    id: 'shophouse',
    category: 'bds',
    name: 'Shophouse',
    icon: '🏬',
    color: '#14B8A6',
    marker_type: 'both',
    property_type: 'shophouse',
    suggested_fields: ['price_text', 'rent_price_text', 'area_text'],
  },

  // Tiện ích templates
  {
    id: 'truong_hoc',
    category: 'tien_ich',
    name: 'Trường học',
    icon: '🏫',
    color: '#3B82F6',
    marker_type: 'custom',
    label_text: 'Trường học',
  },
  {
    id: 'benh_vien',
    category: 'tien_ich',
    name: 'Bệnh viện',
    icon: '🏥',
    color: '#EF4444',
    marker_type: 'custom',
    label_text: 'Bệnh viện',
  },
  {
    id: 'sieu_thi',
    category: 'tien_ich',
    name: 'Siêu thị',
    icon: '🛒',
    color: '#10B981',
    marker_type: 'custom',
    label_text: 'Siêu thị',
  },
  {
    id: 'cong_vien',
    category: 'tien_ich',
    name: 'Công viên',
    icon: '🌳',
    color: '#22C55E',
    marker_type: 'custom',
    label_text: 'Công viên',
  },
  {
    id: 'tram_xe_buyt',
    category: 'tien_ich',
    name: 'Trạm xe buýt',
    icon: '🚌',
    color: '#F59E0B',
    marker_type: 'custom',
    label_text: 'Trạm xe buýt',
  },
  {
    id: 'nha_hang',
    category: 'tien_ich',
    name: 'Nhà hàng',
    icon: '🍽️',
    color: '#F97316',
    marker_type: 'custom',
    label_text: 'Nhà hàng',
  },

  // Khác templates
  {
    id: 'du_an',
    category: 'khac',
    name: 'Dự án mới',
    icon: '🏗️',
    color: '#8B5CF6',
    marker_type: 'custom',
    label_text: 'Dự án mới',
  },
  {
    id: 'gia_tot',
    category: 'khac',
    name: 'Giá tốt',
    icon: '💰',
    color: '#EF4444',
    marker_type: 'custom',
    label_text: 'Giá tốt',
  },
  {
    id: 'hot',
    category: 'khac',
    name: 'HOT',
    icon: '🔥',
    color: '#DC2626',
    marker_type: 'custom',
    label_text: 'HOT',
  },
  {
    id: 'vip',
    category: 'khac',
    name: 'VIP',
    icon: '⭐',
    color: '#F59E0B',
    marker_type: 'custom',
    label_text: 'VIP',
  },
]

function MarkerTemplates({ onSelect, selectedId }) {
  return (
    <div className="space-y-4">
      {TEMPLATE_CATEGORIES.map((category) => {
        const templates = MARKER_TEMPLATES.filter((t) => t.category === category.id)
        const Icon = category.icon

        return (
          <div key={category.id}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {category.label}
              </span>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => onSelect(template)}
                  className={clsx(
                    'flex flex-col items-center gap-1 p-2 rounded-xl transition-all text-center',
                    selectedId === template.id
                      ? 'ring-2 ring-primary-500 bg-primary-50'
                      : 'bg-gray-50 hover:bg-gray-100'
                  )}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                    style={{ backgroundColor: `${template.color}20` }}
                  >
                    {template.icon}
                  </div>
                  <span className="text-[10px] text-gray-600 leading-tight line-clamp-2">
                    {template.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Export templates for use elsewhere
export { MARKER_TEMPLATES, TEMPLATE_CATEGORIES }
export default MarkerTemplates
